import React from 'react';
import { Button, Result } from 'antd';

const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';

// Detects the "stale chunk after a new deploy" failure so we can auto-recover
// by reloading (which pulls a fresh index.html referencing the new chunks).
const isChunkLoadError = (error) => {
	if (!error) return false;
	const name = error.name || '';
	const message = error.message || '';
	return (
		name === 'ChunkLoadError' ||
		/Loading chunk [\d]+ failed/i.test(message) ||
		/Loading CSS chunk/i.test(message) ||
		/dynamically imported module/i.test(message) ||
		// A 404'd chunk is often served as index.html, so JS parses HTML => this error.
		/Unexpected token '<'/i.test(message)
	);
};

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, isChunkError: false };
	}

	static getDerivedStateFromError(error) {
		return { hasError: true, isChunkError: isChunkLoadError(error) };
	}

	componentDidCatch(error, errorInfo) {
		// eslint-disable-next-line no-console
		console.error('ErrorBoundary caught an error:', error, errorInfo);

		if (isChunkLoadError(error)) {
			// Reload once to fetch the freshly-deployed assets. Guard with
			// sessionStorage so a genuinely-broken build can't loop forever.
			if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
				sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
				window.location.reload();
			}
		}
	}

	handleReload = () => {
		sessionStorage.removeItem(CHUNK_RELOAD_KEY);
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			// A chunk error triggers an automatic reload; show a neutral message
			// while that happens (or if the reload was already attempted once).
			if (this.state.isChunkError) {
				return (
					<Result
						status="info"
						title="Updating to the latest version"
						subTitle="Please wait a moment. If this screen does not refresh, click reload."
						extra={
							<Button type="primary" onClick={this.handleReload}>
								Reload
							</Button>
						}
					/>
				);
			}

			return (
				<Result
					status="500"
					title="Something went wrong"
					subTitle="Sorry, an unexpected error occurred while loading this page."
					extra={
						<Button type="primary" onClick={this.handleReload}>
							Reload page
						</Button>
					}
				/>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
